import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · PhantomEventStrip
 * tokens locked from docs/design.md §1 + §7 (SIMULATED pill) + §10 (no extra clay)
 * states covered: empty · idle-populated · latest-matched · latest-spawned · latest-failed · history-open · error
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · no shadows · single clay accent per viewport
 *
 * Single-line bottom-strip mini-panel. `--color-pure-white` bg, hairline
 * `--color-pebble` top border. Shows the most recent phantom-bus event
 * in operator-facing telegraphese. NO clay accent — the animated
 * TripsLayer on the map owns the clay this viewport.
 *
 * Click anywhere on the strip to expand a five-row history panel
 * (`PhantomHistoryList`). Click again to collapse. Keyboard-accessible
 * via Enter/Space; announces via `aria-expanded`.
 */
import { useCallback, useMemo, useState } from "react";
import { SimulatedPill } from "../common/SimulatedPill";
import { PhantomHistoryList } from "./PhantomHistoryList";
const rootStyle = {
    backgroundColor: "var(--color-pure-white)",
    color: "var(--color-deep-ink)",
    borderTop: "1px solid var(--color-pebble)",
    fontFamily: "var(--font-display)",
};
const rowStyle = {
    minHeight: 40,
    padding: "8px 22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    cursor: "pointer",
    outline: "none",
};
function shortId(value, cap = 14) {
    if (!value)
        return "—";
    if (value.length <= cap)
        return value;
    return `${value.slice(0, cap - 1)}…`;
}
function statusWord(status) {
    switch (status) {
        case "matched":
            return "matched";
        case "spawned":
            return "spawned";
        case "failed":
        default:
            return "failed";
    }
}
/**
 * Compose the operator-facing line for the most-recent phantom event.
 *
 *   "Phantom bus <status>: <pax> pax at <stop> · <latency>s · <actor>"
 */
function composeLine(m) {
    const actor = m.virtual_bus_id ?? m.matched_driver_id ?? "no-actor";
    // `spawn_latency_s` is the DARP heuristic compute time (typically < 1ms).
    // Wall-clock trigger → dispatch is a separate metric; not conflated here.
    const computeMs = (m.spawn_latency_s * 1000).toFixed(1);
    const parts = [
        `Phantom bus ${statusWord(m.status)}:`,
        `${m.waiting_pax} pax at ${shortId(m.trigger_stop_id, 18)}`,
        `${computeMs}ms compute`,
        shortId(actor, 20),
    ];
    return parts.join(" · ").replace(/·\s+·/g, "·");
}
function stateDotStyle(state) {
    const base = {
        width: 8,
        height: 8,
        borderRadius: "50%",
        display: "inline-block",
        flexShrink: 0,
    };
    switch (state) {
        case "connected":
            return { ...base, backgroundColor: "var(--color-deep-ink)" };
        case "polling":
            return { ...base, backgroundColor: "var(--color-cool-ash)" };
        case "connecting":
            return { ...base, backgroundColor: "var(--color-cool-ash)", opacity: 0.6 };
        case "disconnected":
        default:
            return { ...base, backgroundColor: "var(--color-cool-ash)", opacity: 0.35 };
    }
}
function stateLabel(state) {
    switch (state) {
        case "connected":
            return "Live";
        case "polling":
            return "Polling";
        case "connecting":
            return "Connecting";
        case "disconnected":
        default:
            return "Offline";
    }
}
export function PhantomEventStrip({ recentMatches, activeMatches, connectionState, lastError, monitoredDrivers = 20, corridorId = "AS-4", }) {
    const [open, setOpen] = useState(false);
    const latest = useMemo(() => (recentMatches.length > 0 ? recentMatches[0] ?? null : null), [recentMatches]);
    const toggle = useCallback(() => setOpen((v) => !v), []);
    const onKey = useCallback((e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
        }
    }, [toggle]);
    const emptyCopy = `Phantom Bus armed · monitoring ${monitoredDrivers} drivers on ${corridorId}`;
    const line = latest ? composeLine(latest) : emptyCopy;
    return (_jsxs("section", { style: rootStyle, "data-testid": "phantom-event-strip", "data-open": open ? "true" : "false", "data-connection-state": connectionState, "aria-label": "Phantom bus event strip", children: [_jsxs("div", { role: "button", tabIndex: 0, "aria-expanded": open, "aria-controls": "phantom-history-panel", onClick: toggle, onKeyDown: onKey, style: rowStyle, "data-testid": "phantom-event-strip-toggle", children: [_jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            minWidth: 0,
                            flex: "1 1 auto",
                        }, children: [_jsx("span", { "aria-hidden": "true", style: stateDotStyle(connectionState), title: stateLabel(connectionState) }), _jsx("span", { style: {
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: "var(--color-deep-ink)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0,
                                    flex: "1 1 auto",
                                }, title: line, "data-testid": "phantom-event-strip-text", children: line }), activeMatches.length > 0 && (_jsxs("span", { style: {
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.14em",
                                    color: "var(--color-cool-ash)",
                                    whiteSpace: "nowrap",
                                }, "data-testid": "phantom-active-count", children: [activeMatches.length, " active"] }))] }), _jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexShrink: 0,
                        }, children: [_jsx("span", { style: {
                                    fontSize: 11,
                                    color: "var(--color-cool-ash)",
                                    fontWeight: 700,
                                    letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                    userSelect: "none",
                                }, "aria-hidden": "true", children: open ? "Hide" : "History" }), _jsx(SimulatedPill, { testId: "phantom-strip-simulated-pill", label: "Simulated" })] })] }), lastError && !open && (_jsx("div", { style: {
                    padding: "0 22px 8px",
                    fontSize: 12,
                    color: "var(--color-cool-ash)",
                }, "data-testid": "phantom-strip-error", children: lastError })), _jsx("div", { id: "phantom-history-panel", children: _jsx(PhantomHistoryList, { matches: recentMatches, open: open }) })] }));
}
